/**
 * Main App component
 */
import { useState, useEffect } from 'react';
import './App.css';
import { BackendSelector } from './components/BackendSelector';
import { LaneManager } from './components/LaneManager';
import { AnalysisResults } from './components/AnalysisResults';
import SentimentFilter from './components/SentimentFilter';
import { SearchBar } from './components/SearchBar';
import { api } from './services/api';
import { AnalysisResult, SearchResult } from './types';
import axios from 'axios';

const STORAGE_KEYS = {
    BACKEND: 'praevisio_backend',
    SENTIMENTS: 'praevisio_sentiments',
    VISIBLE_LANES: 'praevisio_visible_lanes',
    CONFIDENCE: 'praevisio_confidence'
};

function App() {
    const [results, setResults] = useState<AnalysisResult[]>([]);
    const [filteredResults, setFilteredResults] = useState<AnalysisResult[]>([]);
    const [selectedSentiments, setSelectedSentiments] = useState<string[]>([]);
    const [visibleLanes, setVisibleLanes] = useState<string[]>([]);
    const [minConfidence, setMinConfidence] = useState<number>(70);
    const [selectedBackend, setSelectedBackend] = useState<string>('local');
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'info' | 'error' | 'success' } | null>(null);
    const [schedulerStatus, setSchedulerStatus] = useState<{
        running: boolean;
        keywords: string[];
    }>({ running: false, keywords: [] });
    const [backendAvailable, setBackendAvailable] = useState<boolean>(true);
    const [checkingBackend, setCheckingBackend] = useState<boolean>(true);

    // Load from LocalStorage on mount
    useEffect(() => {
        const savedBackend = localStorage.getItem(STORAGE_KEYS.BACKEND);
        if (savedBackend) {
            setSelectedBackend(savedBackend);
        }

        const savedSentiments = localStorage.getItem(STORAGE_KEYS.SENTIMENTS);
        if (savedSentiments) {
            setSelectedSentiments(JSON.parse(savedSentiments));
        }

        const savedLanes = localStorage.getItem(STORAGE_KEYS.VISIBLE_LANES);
        if (savedLanes) {
            setVisibleLanes(JSON.parse(savedLanes));
        }

        const savedConfidence = localStorage.getItem(STORAGE_KEYS.CONFIDENCE);
        if (savedConfidence) {
            setMinConfidence(parseInt(savedConfidence));
        }

        loadResults();
    }, []);

    // Auto-hide toast after 3 seconds
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Save Backend to LocalStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.BACKEND, selectedBackend);
    }, [selectedBackend]);

    // Save Sentiments to LocalStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.SENTIMENTS, JSON.stringify(selectedSentiments));
    }, [selectedSentiments]);

    // Save Confidence to LocalStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.CONFIDENCE, minConfidence.toString());
    }, [minConfidence]);

    useEffect(() => {
        filterResults();
    }, [results, selectedSentiments, visibleLanes, minConfidence]);

    const showToast = (message: string, type: 'info' | 'error' | 'success' = 'info') => {
        setToast({ message, type });
    };

    const handleSearch = async (query: string) => {
        if (!query.trim()) {
            return;
        }

        try {
            setSearching(true);
            setError(null);

            console.log('Sende Search Request:', query);

            const response = await api.post<SearchResult>('/search', {
                keywords: [query]
            });

            if (response.data && response.data.total_count === 0) {
                showToast('No information found for this search', 'info');
                setSearching(false);
                return;
            }

            console.log('Search erfolgreich, lade Latest Results...');

            // Neue Lane hinzufügen
            const newLane = query.trim();
            const updatedLanes = [...new Set([...visibleLanes, newLane])];
            setVisibleLanes(updatedLanes);
            localStorage.setItem(STORAGE_KEYS.VISIBLE_LANES, JSON.stringify(updatedLanes));

            await loadResults();
            showToast('Search completed successfully', 'success');

        } catch (err: any) {
            console.error('Search Error:', err);
            setError(err.response?.data?.detail || err.message || 'Search failed');
            showToast(err.response?.data?.detail || err.message || 'Search failed', 'error');
        } finally {
            setSearching(false);
        }
    };

    const handleRemoveLane = (symbol: string) => {
        const updated = visibleLanes.filter(lane => lane !== symbol);
        setVisibleLanes(updated);
        localStorage.setItem(STORAGE_KEYS.VISIBLE_LANES, JSON.stringify(updated));
    };

    const transformSearchResults = (searchResults: SearchResult[]): AnalysisResult[] => {
        const transformed: AnalysisResult[] = [];

        searchResults.forEach(searchResult => {
            const symbol = searchResult.keywords[0] || 'Unknown';

            searchResult.articles.forEach((article, index) => {
                transformed.push({
                    id: `${searchResult.search_id}-${index}`,
                    symbol: symbol,
                    sentiment: article.sentiment,
                    confidence: Math.abs(article.sentiment_score),
                    summary: article.title,
                    timestamp: article.publish_date,
                    source: article.domain,
                    url: article.url,
                    language: article.language
                });
            });
        });

        return transformed;
    };

    const filterResults = () => {
        // Wenn keine Lanes gespeichert, zeige nichts
        if (visibleLanes.length === 0) {
            setFilteredResults([]);
            return;
        }

        const filtered = results.filter(r => {
            if (!r) return false;

            // Nur gespeicherte Lanes anzeigen
            if (!visibleLanes.includes(r.symbol)) {
                return false;
            }

            // Sentiment Filter
            const matchesSentiment = selectedSentiments.length === 0 ||
                (r.sentiment && selectedSentiments.includes(r.sentiment.toLowerCase()));

            // Confidence Filter
            const confidence = r.confidence ? r.confidence * 100 : 0;
            const matchesConfidence = confidence >= minConfidence;

            return matchesSentiment && matchesConfidence;
        });

        setFilteredResults(filtered);
    };

    const loadResults = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get<SearchResult[]>('/results/latest');

            const transformed = transformSearchResults(response.data || []);
            setResults(transformed);
        } catch (err: any) {
            console.error('Error fetching results:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to load results');
        } finally {
            setLoading(false);
        }
    };

    const loadSchedulerStatus = async () => {
        try {
            const response = await api.get<{
                running: boolean;
                keywords: string[];
            }>('/scheduler/status');
            setSchedulerStatus(response.data);
        } catch (err) {
            console.error('Error loading scheduler status:', err);
        }
    };

    useEffect(() => {
        loadSchedulerStatus();
        const interval = setInterval(loadSchedulerStatus, 180000); // Check every 3 minutes
        return () => clearInterval(interval);
    }, []);

    // Check backend health
    useEffect(() => {
        const checkBackend = async () => {
            try {
                await axios.get(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/health`, {
                    timeout: 5000
                });
                setBackendAvailable(true);
            } catch (error) {
                setBackendAvailable(false);
            } finally {
                setCheckingBackend(false);
            }
        };

        // Initial check
        checkBackend();

        // Check every 30 seconds
        const interval = setInterval(checkBackend, 30000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="app">
            {!backendAvailable && !checkingBackend && (
                <div className="maintenance-banner">
                    <span className="maintenance-icon">⚠️</span>
                    Achtung: Wartung - Bitte Geduld
                </div>
            )}

            <header className="app-header">
                <h1>Praeviso - Trading Analysis</h1>
                <div className="header-controls">
                    <BackendSelector
                        value={selectedBackend}
                        onChange={setSelectedBackend}
                    />
                    <LaneManager
                        visibleLanes={visibleLanes}
                        onLanesChange={(lanes) => {
                            setVisibleLanes(lanes);
                            localStorage.setItem(STORAGE_KEYS.VISIBLE_LANES, JSON.stringify(lanes));
                            loadResults();
                        }}
                    />
                </div>
            </header>

            <main className="app-main">
                <div className="controls-row">
                    <SearchBar onSearch={handleSearch} isSearching={searching} />
                </div>
                <SentimentFilter
                    onSentimentChange={setSelectedSentiments}
                    onConfidenceChange={setMinConfidence}
                    defaultSelectedSentiments={selectedSentiments}
                    defaultConfidence={minConfidence}
                />
                {loading && <div className="loading">Loading...</div>}
                {error && <div className="error">Error: {error}</div>}
                {!loading && !error && (
                    <AnalysisResults
                        results={filteredResults}
                        onRemoveLane={handleRemoveLane}
                    />
                )}
                <div className="scheduler-status">
                    <span className={schedulerStatus.running ? 'status-active' : 'status-inactive'}>
                        Auto-Refresh: {schedulerStatus.running ? 'ON' : 'OFF'}
                    </span>
                    <span>Monitoring: {schedulerStatus.keywords.length} keywords</span>
                </div>
            </main>
        </div>
    );
}

export default App;
